// Shared presentational panel for the "Get the best experience" app-download UI.
// Used by both MobileAppPromptModal (first-visit interstitial on mobile browsers)
// and the standalone GetApp page (/getapp - shareable on any browser).

import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Linking, Image } from 'react-native';
import { UserCircle, Building2, ChevronDown } from 'lucide-react';
import getMobileBrowserPlatform from '../../utils/isMobileBrowser';
import { APP_URLS, AppleIcon, GooglePlayIcon, HuaweiIcon } from '../../constants/appStoreUrls';
import { webColors } from '../../constants/webStyles';
import SupermedicLogo from '../../assets/images/supermedic-logo-web.png';

// platform: 'ios' | 'android' | null. When omitted, it is auto-detected from the
// browser. On desktop (null) the store buttons fall back to a natural order.
const AppDownloadPanel = ({ platform: platformProp, initialExpanded = null }) => {
  const platform = platformProp !== undefined ? platformProp : getMobileBrowserPlatform();
  const [expandedCard, setExpandedCard] = useState(initialExpanded);

  const toggleCard = (cardType) => {
    setExpandedCard(expandedCard === cardType ? null : cardType);
  };

  const handleDownloadApp = (userType, store) => {
    Linking.openURL(APP_URLS[userType][store]);
  };

  const renderAppStoreButtons = (userType) => {
    const appleBtn = (
      <TouchableOpacity
        key="apple"
        style={styles.storeBtn}
        onPress={() => handleDownloadApp(userType, 'apple')}
      >
        <AppleIcon />
        <View style={styles.storeBtnText}>
          <Text style={styles.storeBtnSubtext}>Download on the</Text>
          <Text style={styles.storeBtnTitle}>App Store</Text>
        </View>
      </TouchableOpacity>
    );
    const googleBtn = (
      <TouchableOpacity
        key="google"
        style={styles.storeBtn}
        onPress={() => handleDownloadApp(userType, 'google')}
      >
        <GooglePlayIcon />
        <View style={styles.storeBtnText}>
          <Text style={styles.storeBtnSubtext}>Get it on</Text>
          <Text style={styles.storeBtnTitle}>Google Play</Text>
        </View>
      </TouchableOpacity>
    );
    const huaweiBtn = (
      <TouchableOpacity
        key="huawei"
        style={[styles.storeBtn, styles.storeBtnHuawei]}
        onPress={() => handleDownloadApp(userType, 'huawei')}
      >
        <HuaweiIcon />
        <View style={styles.storeBtnText}>
          <Text style={styles.storeBtnSubtext}>Explore it on</Text>
          <Text style={styles.storeBtnTitle}>AppGallery</Text>
        </View>
      </TouchableOpacity>
    );

    const ordered = platform === 'ios'
      ? [appleBtn, googleBtn, huaweiBtn]
      : platform === 'android'
        ? [googleBtn, appleBtn, huaweiBtn]
        : [appleBtn, googleBtn, huaweiBtn];

    return (
      <View style={styles.appStoreSection}>
        <Text style={styles.appStoreLabel}>Download our app to get started:</Text>
        <View style={styles.appStoreButtons}>{ordered}</View>
      </View>
    );
  };

  return (
    <View style={styles.content}>
      <Image source={SupermedicLogo} style={styles.logo} resizeMode="contain" />
      <Text style={styles.title}>Get the best experience</Text>
      <Text style={styles.subtitle}>Download the Supermedic app</Text>

      {/* Healthcare Professional Card */}
      <View
        style={[
          styles.userTypeCard,
          expandedCard === 'professional' && styles.userTypeCardExpanded,
        ]}
      >
        <TouchableOpacity
          style={styles.cardHeader}
          onPress={() => toggleCard('professional')}
        >
          <View style={[styles.optionIcon, styles.optionIconPink]}>
            <UserCircle size={24} color={webColors.primary} />
          </View>
          <View style={styles.optionContent}>
            <Text style={styles.optionTitle}>Healthcare Professional</Text>
            <Text style={styles.optionDescription}>I'm looking for healthcare jobs</Text>
          </View>
          <View
            style={[
              styles.cardArrow,
              expandedCard === 'professional' && styles.cardArrowExpanded,
            ]}
          >
            <ChevronDown size={20} color="#ccc" />
          </View>
        </TouchableOpacity>
        {expandedCard === 'professional' && renderAppStoreButtons('professional')}
      </View>

      {/* Employer / Facility Card */}
      <View
        style={[
          styles.userTypeCard,
          expandedCard === 'employer' && styles.userTypeCardExpanded,
        ]}
      >
        <TouchableOpacity
          style={styles.cardHeader}
          onPress={() => toggleCard('employer')}
        >
          <View style={[styles.optionIcon, styles.optionIconBlue]}>
            <Building2 size={24} color="#2563eb" />
          </View>
          <View style={styles.optionContent}>
            <Text style={styles.optionTitle}>Employer / Facility</Text>
            <Text style={styles.optionDescription}>I want to post jobs and hire professionals</Text>
          </View>
          <View
            style={[
              styles.cardArrow,
              expandedCard === 'employer' && styles.cardArrowExpanded,
            ]}
          >
            <ChevronDown size={20} color="#ccc" />
          </View>
        </TouchableOpacity>
        {expandedCard === 'employer' && renderAppStoreButtons('employer')}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  content: {
    width: '100%',
    maxWidth: 420,
    alignItems: 'stretch',
    gap: 14,
  },
  logo: {
    width: 180,
    height: 60,
    marginBottom: 16,
    alignSelf: 'center',
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#1a1a1a',
    textAlign: 'center',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 15,
    color: '#666',
    textAlign: 'center',
    marginBottom: 10,
  },
  // Option Icons with gradients
  optionIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionIconPink: {
    background: 'linear-gradient(135deg, #ffe4ef 0%, #ffd4e8 100%)',
    backgroundColor: '#ffe4ef',
  },
  optionIconBlue: {
    background: 'linear-gradient(135deg, #e4f0ff 0%, #d4e8ff 100%)',
    backgroundColor: '#e4f0ff',
  },
  optionContent: {
    flex: 1,
  },
  optionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 2,
  },
  optionDescription: {
    fontSize: 13,
    color: '#888',
  },
  // User Type Cards (Expandable)
  userTypeCard: {
    backgroundColor: '#fafafa',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#eee',
    overflow: 'hidden',
  },
  userTypeCardExpanded: {
    borderColor: '#ddd',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    padding: 16,
    cursor: 'pointer',
  },
  cardArrow: {
    transition: 'transform 0.2s ease',
  },
  cardArrowExpanded: {
    transform: [{ rotate: '180deg' }],
  },
  // App Store Section
  appStoreSection: {
    padding: 18,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
    backgroundColor: '#fff',
  },
  appStoreLabel: {
    fontSize: 13,
    color: '#666',
    fontWeight: '500',
    marginBottom: 14,
  },
  appStoreButtons: {
    gap: 10,
  },
  storeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    paddingHorizontal: 16,
    backgroundColor: '#1a1a1a',
    borderRadius: 12,
    cursor: 'pointer',
  },
  storeBtnHuawei: {
    backgroundColor: '#c7112b',
  },
  storeBtnText: {
    flexDirection: 'column',
  },
  storeBtnSubtext: {
    fontSize: 10,
    color: '#aaa',
    lineHeight: 14,
  },
  storeBtnTitle: {
    fontSize: 14,
    color: '#fff',
    fontWeight: '600',
    lineHeight: 18,
  },
});

export default AppDownloadPanel;
